import {
  Box,
  Container,
  Divider,
  MenuItem,
  Select,
  SelectChangeEvent,
  styled,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { palette } from "@platform/design-system";
import { Link } from "react-router";

type Props = {
  route: string;
  dueDate: string;
  effectiveDate: string;
  stageSelected: string;
  onStageSelected: (params: { status: string }) => void;
  onDocumentClick: VoidFunction;
  rfpStatuses: Record<string, string>;
};

const NavigationBar = ({
  route,
  dueDate,
  effectiveDate,
  onStageSelected,
  onDocumentClick,
  stageSelected,
  rfpStatuses,
}: Props): React.JSX.Element => (
  <Container
    maxWidth="xl"
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    }}
  >
    <StyledTabs
      value={route}
      aria-label="rfp details navigation tabs"
      variant="scrollable"
      scrollButtons="auto"
      allowScrollButtonsMobile
    >
      <Tab label="WORKBENCH" value="workbench" to="" component={Link} />
      <Tab
        label="CARRIER TRACKER"
        value="carrier-tracker"
        to="carrier-tracker"
        component={Link}
      />
      <Tab label="DOCUMENTS" onClick={onDocumentClick} />
    </StyledTabs>
    <Box display="flex" alignItems="center">
      <DatesSection dueDate={dueDate} effectiveDate={effectiveDate} />
      <Divider orientation="vertical" flexItem sx={{ marginLeft: 1.75 }} />
      <StyledSelect
        id="stage-select"
        sx={{ textAlign: "center" }}
        value={stageSelected}
        onChange={(e) => {
          const newValue = (e as SelectChangeEvent<string>).target.value;
          if (newValue !== stageSelected) {
            onStageSelected({ status: newValue });
          }
        }}
      >
        {Object.entries(rfpStatuses).map(([value, label]) => (
          <MenuItem key={value} value={value}>
            {label}
          </MenuItem>
        ))}
      </StyledSelect>
    </Box>
  </Container>
);

const DatesSection = ({
  dueDate,
  effectiveDate,
}: {
  dueDate: string;
  effectiveDate: string;
}): React.JSX.Element => (
  <Box display="flex" gap={{ md: 4, lg: 6 }}>
    <DateDisplay title="Due Date" subTitle={dueDate} />
    <DateDisplay title="Effective Date" subTitle={effectiveDate} />
  </Box>
);

const DateDisplay = ({
  title,
  subTitle,
}: {
  title: string;
  subTitle: string;
}): React.JSX.Element => (
  <Box>
    <Typography variant="small" whiteSpace="nowrap">
      {title}
    </Typography>
    <Typography variant="smallBold" display="block">
      {subTitle}
    </Typography>
  </Box>
);

const StyledTabs = styled(Tabs)(() => ({
  minHeight: 0,
  width: "100%",
  ".MuiButtonBase-root": {
    minHeight: 48,
    color: `${palette.brandSupporting.medium} !important`,
    padding: "0 1rem",
    "&:not(:first-of-type)": {
      marginLeft: 0,
    },
    "&:not(.Mui-selected) .error": {
      color: palette.error.main,
    },
  },
  ".MuiTabs-indicator": {
    backgroundColor: palette.brandSupporting.medium,
  },
})) as unknown as typeof Tabs;

const StyledSelect = styled(Select)(() => ({
  ".MuiOutlinedInput-input": {
    paddingRight: "56px !important",
  },
  ".MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  ".MuiSelect-icon": {
    border: `1px solid ${palette.brandPrimary.main}`,
    padding: 4,
    borderRadius: 4,
  },
})) as unknown as typeof Select;

export default NavigationBar;
